import React from "react";
import Footer from "../../utils/footer";
import CompanyInformation from "../common/CompanyInformation";
import TrustSection from "../common/TrustSection";
import BusinessVerification from "../common/BusinessVerification";
import ContactCTA from "../common/ContactCTA";
import PageSeo from "../common/PageSeo";


const About = () => {
  return (
    <main className="pt-28 bg-gray-50 text-gray-800">
      <PageSeo
        title="About Us"
        description="Learn about Thakkhar Realtors, our mission, vision, values, professional team, and business transparency practices."
      />

      {/* ====== PAGE HEADER ====== */}
      <section className="text-center py-12 bg-white shadow-sm">
        <h1 className="text-3xl md:text-4xl font-bold text-blue-700">
          Our Mission Is To Find Your Dream Home - Thakkhar Realtors
        </h1>
        <p className="mt-4 text-gray-600 max-w-3xl mx-auto">
          Thakkhar Realtors is an outcome of more than 10 years of experience in
          the Real Estate Industry. We are a team of Real Estate Experts, Legal
          Advisors, and Property Managers committed to making property buying,
          selling, and renting smooth, transparent, and trustworthy.
        </p>
      </section>

      <section className="max-w-6xl mx-auto py-12 px-6 grid md:grid-cols-2 gap-8">
        <article className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">Company History</h2>
          <p className="text-gray-600 leading-relaxed">
            Over the last decade, Thakkhar Realtors has advised home buyers,
            sellers, and investors across Panvel and Navi Mumbai with a practical,
            regulation-aware approach to property transactions.
          </p>
        </article>
        <article className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">Professional Team</h2>
          <p className="text-gray-600 leading-relaxed">
            Our team includes experienced real estate advisors, legal support
            partners, and relationship managers focused on transparent and timely
            communication at every stage.
          </p>
        </article>
      </section>

      {/* ====== FOUNDER SECTION (TEXT ONLY, IMAGE REMOVED) ====== */}
      <section className="max-w-5xl mx-auto py-14 px-6 text-center">
        <h2 className="text-2xl font-semibold text-gray-800 mb-3">
          Mr. Chirag Ashok Thakkar
        </h2>
        <p className="text-gray-600 leading-relaxed max-w-3xl mx-auto">
          A certified MahaRERA Real Estate Agent with over a decade of
          experience, Chirag Thakkar leads Thakkar Realtors with a vision to
          bring professionalism, ethics, and customer satisfaction to the
          forefront of real estate services. His expertise covers residential,
          commercial, and investment property segments across Mumbai and Navi
          Mumbai.
        </p>
      </section>

      {/* ====== VISION & MISSION ====== */}
      <section className="max-w-6xl mx-auto py-10 px-6 grid md:grid-cols-2 gap-8">
        <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100">
          <h2 className="text-2xl font-semibold text-blue-700 mb-3">Vision</h2>
          <p className="text-gray-600">
            To set a new standard for excellence in personalized property
            journeys, driven by trust and transparency.
          </p>
        </div>

        <div className="bg-blue-600 text-white p-8 rounded-lg shadow-sm">
          <h2 className="text-2xl font-semibold mb-3">Mission</h2>
          <p>
            Empowering dreams through seamless real estate experiences that
            combine integrity, innovation, and customer delight.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto py-4 px-6">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              title: "Core Values",
              text: "Integrity, accountability, and client-first communication in every transaction.",
            },
            {
              title: "Why Choose Us",
              text: "Verified listings, practical advisory, and complete process clarity from inquiry to possession.",
            },
            {
              title: "Business Transparency",
              text: "Clear disclosures, documented communication, and policy-compliant marketing practices.",
            },
          ].map((item) => (
            <article key={item.title} className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900">{item.title}</h3>
              <p className="mt-2 text-sm text-slate-600">{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      {/* ====== CERTIFICATES SECTION ====== */}
      <section className="bg-white py-12 px-6 text-center">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">
          Authorized & Certified Real Estate Agent
        </h2>
        <p className="text-gray-600 max-w-3xl mx-auto mb-10">
          Thakkar Realtors is a registered MahaRERA real estate agent and has
          successfully completed the official MahaRERA Real Estate Agent
          Training Program, ensuring clients receive trusted and compliant
          service.
        </p>

        <div className="grid md:grid-cols-2 gap-10 max-w-5xl mx-auto">
          <img
            src="/uploads/c08f991c-9cf5-4c6f-bbc4-d201a3b699d3.png"
            alt="MahaRERA Certificate"
            className="rounded-lg shadow-md border border-gray-200"
          />
          <img
            src="/uploads/c5c375ef-14ad-43da-a720-3b438a32be5e.png"
            alt="RERA License"
            className="rounded-lg shadow-md border border-gray-200"
          />
        </div>
      </section>

      {/* ====== STATS SECTION ====== */}
      <section className="max-w-6xl mx-auto text-center py-14 px-6 border-t border-gray-100">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-gray-700">
          {[
            ["80,123+", "Satisfied Customers"],
            ["400+", "Managed Apartments"],
            ["300+", "Available Inventories"],
            ["50+", "Resale Deals"],
          ].map(([num, label], i) => (
            <div key={i}>
              <h3 className="text-3xl font-bold text-blue-700">{num}</h3>
              <p>{label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ====== WHY CHOOSE US ====== */}
      <section className="max-w-6xl mx-auto py-12 px-6 text-center">
        <h2 className="text-2xl font-bold text-gray-800 mb-10">
          Why Choose Thakkhar Realtors
        </h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="p-8 bg-white rounded-xl shadow hover:shadow-lg transition border border-gray-100">
            <div className="text-4xl mb-4">🏡</div>
            <h3 className="text-lg font-semibold mb-2">Trusted By Thousands</h3>
            <p className="text-gray-600">
              Every transaction reflects our dedication to transparency,
              commitment, and client satisfaction.
            </p>
          </div>
          <div className="p-8 bg-white rounded-xl shadow hover:shadow-lg transition border border-gray-100">
            <div className="text-4xl mb-4">🏙️</div>
            <h3 className="text-lg font-semibold mb-2">
              Wide Range Of Properties
            </h3>
            <p className="text-gray-600">
              From luxury apartments to affordable housing — we have homes for
              every lifestyle and budget.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-8">
        <TrustSection title="Trust Indicators" />
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-8">
        <BusinessVerification />
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-8">
        <CompanyInformation />
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-16">
        <ContactCTA />
      </section>

      {/* ====== FOOTER ====== */}
      <Footer />
    </main>
  );
};

export default About;
